/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useState, useMemo, useEffect } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm, FormProvider, UnpackNestedValue } from "react-hook-form";
import { v4 as uuidv4 } from "uuid";
import { Sidebar, Button, theme, Text } from "@nordcloud/gnui";
import { usePeopleQuery } from "~/generated/graphql";
import { LoaderWrap } from "~/components";
import { isNonEmpty, isNotEmpty } from "~/tools";
import { WorkflowDetails } from "../AddWorkflow/WorkflowDetailsForm";
import { WorkflowCloneProps } from "../types";
import { FormField, FormData, schema } from "./formConfig";

type Props = {
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
  data?: WorkflowCloneProps | null;
  onSubmit: (input: UnpackNestedValue<FormData>) => void;
};

export function CloneWorkflowSidebar({
  isOpen,
  setIsOpen,
  data,
  onSubmit,
}: Props) {
  const { loading: peopleLoading } = usePeopleQuery({ skip: !isOpen });

  const defaultValues: UnpackNestedValue<FormData> = useMemo(() => {
    const defaultContactIds: UnpackNestedValue<string[]> =
      data?.contactPersons.map(({ id }) => id) ?? [];
    return {
      [FormField.NAME]: `Clone_${data?.name}`,
      [FormField.DESCRIPTION]: data?.description ?? "",
      [FormField.CONTACT_PERSON_IDS]: defaultContactIds.map((id) => ({
        value: id,
      })),
      [FormField.ACTIVE]: data?.active ?? false,
    };
  }, [data]);

  const formMethods = useForm<UnpackNestedValue<FormData>>({
    mode: "onChange",
    resolver: yupResolver(schema),
    defaultValues,
  });

  const {
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = formMethods;

  const [contactPersonSlots, setContactPersonSlots] = useState([
    {
      contactPersonId: "",
      slotId: uuidv4(),
    },
  ]);

  useEffect(() => {
    if (isSubmitting) {
      const cleanContactPersonSlots = contactPersonSlots.filter(
        (contactPersonSlot) => isNotEmpty(contactPersonSlot.contactPersonId)
      );

      if (isNonEmpty(cleanContactPersonSlots)) {
        setContactPersonSlots(cleanContactPersonSlots);
      }
    }
  }, [isSubmitting]);

  useEffect(() => {
    reset(defaultValues);

    setContactPersonSlots(
      data?.contactPersons.map(({ id }) => ({
        contactPersonId: id,
        slotId: uuidv4(),
      })) ?? [
        {
          contactPersonId: "",
          slotId: uuidv4(),
        },
      ]
    );
  }, [reset, data, defaultValues]);

  return (
    <>
      <Sidebar
        title="Clone Workflow"
        isOpen={isOpen}
        onClick={() => {
          setIsOpen(false);
          reset();
        }}
      >
        <LoaderWrap inContent loading={peopleLoading}>
          <FormProvider {...formMethods}>
            <form id="cloneWorkflowForm" onSubmit={handleSubmit(onSubmit)}>
              <Text size="xs" color={theme.color.text.text02}>
                This information can be changed later. Cloned workflow will be
                inactive by default.
              </Text>
              <WorkflowDetails
                contactPersonSlots={contactPersonSlots}
                setContactPersonSlots={setContactPersonSlots}
              />
              <Button
                type="submit"
                mt={theme.spacing.spacing04}
                form="cloneWorkflowForm"
              >
                Clone
              </Button>
            </form>
          </FormProvider>
        </LoaderWrap>
      </Sidebar>
    </>
  );
}
